/**
 * AddDeductionModal Component
 * Records a project-level deduction (refund or late penalty interest).
 * These credit the client and reduce the outstanding balance.
 */

import React, { useState } from 'react';
import { paymentDeductionAPI } from '../../services/api';
import { toast } from 'react-toastify';

const DEDUCTION_TYPES = [
  { value: 'refund', label: 'Refund' },
  { value: 'late_penalty_interest', label: 'Late Penalty Interest' }
];

const AddDeductionModal = ({ isOpen, onClose, onSuccess, projectId }) => {
  const today = new Date().toISOString().slice(0, 10);

  const [type, setType] = useState('refund');
  const [amount, setAmount] = useState('');
  const [deductionDate, setDeductionDate] = useState(today);
  const [reason, setReason] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const resetForm = () => {
    setType('refund');
    setAmount('');
    setDeductionDate(today);
    setReason('');
    setReferenceNumber('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsed = parseFloat(amount);
    if (!parsed || parsed <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      await paymentDeductionAPI.create(projectId, {
        type,
        amount: parsed,
        deductionDate,
        reason: reason || null,
        referenceNumber: referenceNumber || null
      });
      toast.success('Deduction recorded');
      resetForm();
      onSuccess();
    } catch (err) {
      console.error('Error creating deduction:', err);
      toast.error(err.response?.data?.error || 'Failed to create deduction');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-500 bg-opacity-75 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0" onClick={handleClose} />
      <div className="relative bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg sm:mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <form onSubmit={handleSubmit} className="flex flex-col max-h-[90vh]">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Add Deduction
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Refunds and late penalty interest credit the client and reduce the remaining balance.
            </p>
          </div>

          <div className="px-4 sm:px-6 py-4 overflow-y-auto space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="text-red-500">*</span>
              </label>
              <select
                value={type}
                onChange={e => setType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                {DEDUCTION_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount (RM) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={deductionDate}
                  onChange={e => setDeductionDate(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
              <input
                type="text"
                value={referenceNumber}
                onChange={e => setReferenceNumber(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="e.g. refund cheque #, transfer reference"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
              <textarea
                rows={3}
                value={reason}
                onChange={e => setReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="Why is this deduction being recorded?"
              />
            </div>
          </div>

          <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row gap-2 sm:justify-end flex-shrink-0">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
            >
              {submitting ? 'Saving…' : 'Add Deduction'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDeductionModal;
